import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { camelCase } from 'lodash';

import { useCheckImportPartsStatus } from 'coreOld/hooks';
import { getListPayload, removeUnderscore } from 'coreOld/utils';
import { ModalStep } from 'partsOld/enums';
import {
  useGetImportPreviewColumns,
  useGetImportRows,
  useGetImportValidateManufacturers,
  useGetImportValidateParts,
  useImportPartsActions,
} from 'partsOld/hooks';

import { previewSavePartsStatus } from 'partsOld/services/imports';

import PreviewModalView from './PreviewModalView';

interface IProps {
  partImport: number;
  data;
  setStep: Dispatch<SetStateAction<ModalStep>>;
}

const getPayload = (partImport: number) => {
  return getListPayload({}, { partImport, pageSize: 10 });
};

const PreviewModal = ({ partImport, data, setStep }: IProps) => {
  const [isComponentRendered, setIsComponentRendered] = useState(false);
  const [isUnknownManufaturers, setIsUnknownManufactures] = useState(false);
  const [isIdentifyManufaturersOpen, setIsIdentifyManufacturerOpen] = useState(false);

  useEffect(() => {
    if (isComponentRendered) {
      onInit();
    }
  }, [isComponentRendered]);

  useEffect(() => {
    setIsComponentRendered(true);
  }, []);

  const onImportColumnsSuccess = () => {
    if (isUnknownManufaturers) {
      return;
    }

    refetchRows();
  };

  const onImportValidatePartsSuccess = (manufacturers) => {
    if (manufacturers > 0) {
      setIsUnknownManufactures(true);

      return;
    }

    refetchPreviewColumns();
  };

  const onValidateManufacturersSuccess = (manufacturers) => {
    if (manufacturers > 0) {
      setIsIdentifyManufacturerOpen(true);

      return;
    }

    refetchValidateParts();
  };

  const { startCheckingStatus } = useCheckImportPartsStatus(partImport, setStep);

  const { importColumnNameAliases, importPreviewSaveParts, importParts } = useImportPartsActions();

  const {
    records,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    refetch: refetchRows,
  } = useGetImportRows(getPayload(partImport), false);

  const {
    records: columnsData,
    isLoading: isLoadingColumns,
    refetch: refetchPreviewColumns,
  } = useGetImportPreviewColumns({ partImport }, false, () => onImportColumnsSuccess());

  const { refetchValidateParts, isValidatePartsLoading } = useGetImportValidateParts(
    {
      partImport,
    },
    false,
    (data) => onImportValidatePartsSuccess(data)
  );

  const {
    result: unknownManufacturers,
    refetchValidateManufacturers,
    isValidateManufacturerLoading,
  } = useGetImportValidateManufacturers({ partImport }, (data) =>
    onValidateManufacturersSuccess(data)
  );

  const columns = useMemo(() => {
    return columnsData?.map((item) => {
      return {
        Header: removeUnderscore(item),
        accessor: camelCase(item),
      };
    });
  }, [columnsData]);

  const onInit = async () => {
    const { columnAliases, previewSavePartsData } = data;
    await importColumnNameAliases({ aliases: columnAliases });
    await importPreviewSaveParts(previewSavePartsData);

    await previewSavePartsStatus({ partImport: data.partImport });
    refetchValidateManufacturers();
  };

  const goBack = () => {
    setStep(ModalStep.COLUMNS_HEADERS);
  };

  const onImport = async () => {
    await importParts({ partImport, adjustQuantity: 0 });
    startCheckingStatus();
  };

  return (
    <PreviewModalView
      {...{
        isLoading:
          isLoading || isLoadingColumns || isValidateManufacturerLoading || isValidatePartsLoading,
        records,
        columns,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        goBack,
        isChooseManufacturerModalOpen: isUnknownManufaturers,
        isIdentifyManufaturersOpen,
        setIsUnknownManufactures,
        onImport,
        partImport,
        refetchColumns: refetchPreviewColumns,
        refetchValidateParts,
        unknownManufacturers,
      }}
    />
  );
};

export default PreviewModal;
